import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  Paper,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Stack,
  CircularProgress,
} from "@mui/material";
import CelebrationIcon from "@mui/icons-material/EmojiEvents";
import SadIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import CloseIcon from "@mui/icons-material/Close";
import confetti from "canvas-confetti"; // Ensure you have installed canvas-confetti
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAppStore } from "../../../appStore";
import axios from "axios";
import { api_url } from "../../../apiUtils";
import { useTheme, useMediaQuery } from "@mui/material";
import Slide from "@mui/material/Slide";
import Grow from "@mui/material/Grow";
import { getDeviceId } from "../../utils/DeviceId";
import { pingGameSession } from "../../utils/GameServices";
import { toast } from "react-toastify";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

// Dice pattern for displaying dots
const diceDots = {
  1: [[1, 1]],
  2: [
    [0, 0],
    [2, 2],
  ],
  3: [
    [0, 0],
    [1, 1],
    [2, 2],
  ],
  4: [
    [0, 0],
    [0, 2],
    [2, 0],
    [2, 2],
  ],
  5: [
    [0, 0],
    [0, 2],
    [1, 1],
    [2, 0],
    [2, 2],
  ],
  6: [
    [0, 0],
    [1, 0],
    [2, 0],
    [0, 2],
    [1, 2],
    [2, 2],
  ],
};

// Fire confetti effect
const fireConfetti = () => {
  var duration = 2 * 1000; // 5 seconds duration
  var animationEnd = Date.now() + duration;
  var defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 1000 };

  function randomInRange(min, max) {
    return Math.random() * (max - min) + min;
  }

  var interval = setInterval(function () {
    var timeLeft = animationEnd - Date.now();

    if (timeLeft <= 0) {
      return clearInterval(interval); // Stop the confetti after 5 seconds
    }

    confetti({
      ...defaults,
      particleCount: 50,
      origin: {
        x: randomInRange(0.1, 0.9),
        y: Math.random() - 0.2,
      },
    });
  }, 200); // Every 200ms, fire a burst of confetti
};

const Dice = ({ value }) => (
  <Box
    sx={{
      width: 60,
      height: 60,
      backgroundColor: "#fff",
      borderRadius: 2,
      display: "grid",
      gridTemplateRows: "repeat(3, 1fr)",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: "2px",
      padding: "5px",
      boxShadow: 3,
      transition: "all 0.2s ease-in-out",
    }}
  >
    {Array.from({ length: 9 }).map((_, i) => {
      const row = Math.floor(i / 3);
      const col = i % 3;
      const showDot = diceDots[value]?.some(([r, c]) => r === row && c === col);
      return (
        <Box
          key={i}
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {showDot && (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "#333",
              }}
            />
          )}
        </Box>
      );
    })}
  </Box>
);

const AnimatedDice = ({ rolling, finalValue }) => {
  const [displayedValue, setDisplayedValue] = useState(finalValue);

  useEffect(() => {
    if (!rolling) {
      setDisplayedValue(finalValue);
      return;
    }

    const interval = setInterval(() => {
      setDisplayedValue(Math.floor(Math.random() * 6) + 1);
    }, 1);

    return () => clearInterval(interval);
  }, [rolling, finalValue]);

  return <Dice value={displayedValue} />;
};

const DiceRoller = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [diceCount, setDiceCount] = useState(3);
  const [chosenNumber, setChosenNumber] = useState(3);
  const [rolling, setRolling] = useState(false);
  const [finalResults, setFinalResults] = useState([]);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [didWin, setDidWin] = useState(false);
  const [winAmount, setWinAmount] = useState(0);
  const [previousBet, setPreviousBet] = useState(0);
  const [previousOutcome, setPreviousOutcome] = useState(null);
  const [canStop, setCanStop] = useState(false);
  const [loadingResult, setLoadingResult] = useState(false);
  const {
    setWalletBalance,
    profileData,
    diceTimerStartedAt,
    setDiceTimerStartedAt,
    diceTimerId,
    setDiceTimerId,
    setDiceMinute,
    diceMinute,
  } = useAppStore();
  const [gameSettings, setGameSettings] = useState({
    dice: { forcedWinCycleCount: null, winningRatio: null },
    spin: { forcedWinCycleCount: null, winningRatio: null },
    direction: { forcedWinCycleCount: null, winningRatio: null },
  });

  const betAmountMap = {
    3: 5,
    4: 10,
    5: 15,
  };

  const betAmount = betAmountMap[diceCount];

  const total = finalResults.reduce((sum, val) => sum + val, 0);
  const minTotal = diceCount;
  const maxTotal = diceCount * 6;
  const [countdown, setCountdown] = useState(7);
  const countdownDuration = 7; // seconds
  const countdownIntervalId = useRef(null); // useRef for safe interval cleanup
  const deviceId = getDeviceId();
  const sessionToken = sessionStorage.getItem("sessionId");

  const startRolling = () => {
    setRolling(true);
    setFinalResults([]);
    setCountdown(countdownDuration);

    // Clear any existing interval just in case
    if (countdownIntervalId.current) clearInterval(countdownIntervalId.current);

    countdownIntervalId.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownIntervalId.current);
          stopRolling();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const stopRolling = async () => {
    if (countdownIntervalId.current) clearInterval(countdownIntervalId.current);
    setLoadingResult(true); // show spinner or “processing result”

    try {
      const response = await axios.post(
        `${api_url}game/dice-roll`,
        {
          userId: profileData?._id,
          diceCount,
          chosenNumber,
          betAmount,
          // totalScore: finalResults.reduce((sum, val) => sum + val, 0), // We no longer generate totalScore here, it's from the backend
        },
        {
          headers: {
            accesstoken: sessionStorage.getItem("accesstoken"),
          },
        }
      );
      if (response.data.success) {
        const { didWin, winAmount, outcome, balance, totalScore, results } =
          response.data;
        setDidWin(didWin);
        setWinAmount(winAmount);
        setWalletBalance(balance);
        setFinalResults(results); // Set the dice results from the backend
        setShowResultDialog(true);
        if (didWin) fireConfetti();
        setPreviousBet(betAmount);
        setPreviousOutcome(outcome);
        setRolling(false);
      } else {
        toast.error("Game error:", response?.data?.message);
      }
    } catch (error) {
      console.error("Error during dice roll:", error);
      toast.error(
        error?.response?.data?.message || "An error occurred during the dice roll."
      );
      setLoadingResult(true);
    } finally {
      setRolling(false);
      setLoadingResult(false); // hide spinner after response
    }
  };

  // Cleanup interval on unmount (to be safe)
  useEffect(() => {
    return () => {
      if (countdownIntervalId.current)
        clearInterval(countdownIntervalId.current);
    };
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${api_url}auth/get-auto-game-settings`, {
          headers: {
            accessToken: sessionStorage.getItem("accesstoken"),
          },
        });

        const data = res.data.data;
        console.log(data);

        const newSettings = {
          dice: { forcedWinCycleCount: 0, winningRatio: 0 },
          spin: { forcedWinCycleCount: 0, winningRatio: 0 },
          direction: { forcedWinCycleCount: 0, winningRatio: 0 },
        };

        data.forEach((s) => {
          if (s.key === "dice") {
            newSettings.dice = {
              forcedWinCycleCount: s.forcedWinCycleCount,
              winningRatio: s.winningRatio,
            };
            setDiceMinute(s.minutes);
            console.log("dice settings", newSettings.dice);
          } else if (s.key === "spin") {
            newSettings.spin = {
              forcedWinCycleCount: s.forcedWinCycleCount,
              winningRatio: s.winningRatio,
            };

            console.log("spin settings", newSettings.spin);
          } else if (s.key === "direction") {
            newSettings.direction = {
              forcedWinCycleCount: s.forcedWinCycleCount,
              winningRatio: s.winningRatio,
            };

            console.log("direction settings", newSettings.direction);
          }
        });

        setGameSettings(newSettings);
      } catch (err) {
        console.error("Failed to load auto game settings", err);
      }
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      pingGameSession("dice-roll");
    }, 2 * 60 * 1000); // every 2 mins

    return () => clearInterval(interval);
  }, []);

  console.log("minute", diceMinute);

  useEffect(() => {
    startDiceTimer();
    return () => clearTimeout(diceTimerId);
  }, []); // runs only once on mount

  function startDiceTimer() {
    const delay = (diceMinute || 5) * 60 * 1000;
    const now = Date.now();

    // If timer already started, compute remaining
    if (diceTimerStartedAt) {
      const elapsed = now - diceTimerStartedAt;
      const remaining = delay - elapsed;

      if (remaining > 0) {
        console.log(`⏱️ Resuming dice timer with ${remaining / 1000}s left`);
        const timeoutId = setTimeout(callUpdateCycle, remaining);
        setDiceTimerId(timeoutId);
        return;
      }
    }

    console.log(
      `⏱️ Starting new dice cycle timer for ${delay / 60000} minutes`
    );
    const timeoutId = setTimeout(callUpdateCycle, delay);
    setDiceTimerId(timeoutId);
    setDiceTimerStartedAt(now);
  }

  async function callUpdateCycle() {
    try {
      console.log("⏰ Timer reached! Calling update-dice-cycle API...");
      await axios.put(
        `${api_url}game/update-dice-cycle`,
        {
          userId: profileData?._id,
          deviceId,
          sessionToken,
        },
        {
          headers: { accesstoken: sessionStorage.getItem("accesstoken") },
        }
      );
      console.log("✅ update-dice-cycle API called successfully");

      // 🔁 Restart timer for next cycle automatically
      setDiceTimerStartedAt(Date.now());
      startDiceTimer();
    } catch (error) {
      console.error("❌ Error calling update-dice-cycle API:", error);
    }
  }

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", mb: 4, ml: 2 }}>
        <ArrowBackIcon
          sx={{
            color: { xs: "#FFFFFF", md: "#ff" },
            border: "1px solid #fff",
            backgroundColor: { xs: "#fff", md: "transparent" },
            borderRadius: 2,
            "&:hover": {
              backgroundColor: "#1E2466",
              color: "#FFFFFF",
            },
            cursor: "pointer",
          }}
          onClick={async () => {
            try {
              await axios.post(
                `${api_url}game/extra-game/end-session`,
                {
                  gameId: "dice-roll",
                  deviceId: getDeviceId(),
                  sessionToken: sessionStorage.getItem("sessionId"),
                },
                {
                  headers: {
                    accesstoken: sessionStorage.getItem("accesstoken"),
                  },
                }
              );

              // After session ends, navigate back
              navigate("/home/menus");
            } catch (err) {
              console.error("Failed to end session on back", err);
              navigate("/home/menus"); // Fallback navigation
            }
          }}
        />
        <Typography
          sx={{
            fontWeight: "bold",
            fontSize: { xs: "20px", md: "24px" },
            ml: 1,
            color: "#fff",
          }}
        >
          DICE ROLL [ 1 : {gameSettings.dice.winningRatio} ]
        </Typography>
      </Box>
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: { xs: 0, sm: 2 },
            mb: 3,
          }}
        >
          <Box
            sx={{
              height: { xs: "70px", sm: "80px" },
              width: { xs: "200px", sm: "200px" },
            }}
          >
            <img
              src="/dicerolllogo.png"
              alt="Dice Roll Logo"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                display: "block",
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            // background: "rgba(0, 0, 0, 0.5)", // semi-transparent background
            borderRadius: 2,
            p: 2,
            mx: "auto",
            maxWidth: 600,
            width: "90%", // responsive width
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              flexDirection: { xs: "row", sm: "row" },
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              width: "100%",
            }}
          >
            <TextField
              label="Dice Count"
              select
              value={diceCount}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                setDiceCount(val);
                setChosenNumber(val);
              }}
              SelectProps={{
                native: true,
                sx: {
                  color: "black",
                  pointerEvents: rolling ? "none" : "auto", // 🚀 Block interaction when rolling
                },
              }}
              size="small"
              fullWidth
              sx={{
                maxWidth: { xs: 100, sm: 150 },
                "& .MuiInputBase-root": {
                  height: { xs: 50, sm: 60 },
                  color: "#FFD80A",
                  background: "rgba(255, 255, 255, 0.1)",
                  borderRadius: 1,
                  fontSize: "2rem",
                  textAlign: "center",
                  fontWeight: "bolder",
                },
                "& .MuiInputLabel-root": {
                  color: "#FFD80A",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#FFD80A",
                },
                "& .MuiSvgIcon-root": {
                  color: "#FFD80A",
                },
                "& option": {
                  color: "#000",
                  fontSize: "0.9rem",
                },
                "& .MuiInputBase-input": {
                  textAlign: "center",
                },
              }}
              InputProps={{
                readOnly: rolling, // 🚀 Prevent typing
              }}
            >
              {Array.from({ length: 3 }, (_, i) => i + 3).map((num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
            </TextField>

            <TextField
              label="Choose a Number"
              select
              size="small"
              value={chosenNumber}
              onChange={(e) => setChosenNumber(Number(e.target.value))}
              SelectProps={{
                native: true,
                sx: {
                  color: "black",
                  pointerEvents: rolling ? "none" : "auto", // 🚀 Block interaction when rolling
                },
              }}
              fullWidth
              sx={{
                maxWidth: { xs: 100, sm: 150 },
                "& .MuiInputBase-root": {
                  height: { xs: 50, sm: 60 },
                  color: "#FFD80A",
                  background: "rgba(255, 255, 255, 0.1)",
                  borderRadius: 1,
                  fontSize: "2rem",
                  textAlign: "center",
                  fontWeight: "bolder",
                },
                "& .MuiInputLabel-root": {
                  color: "#FFD80A",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#FFD80A",
                },
                "& .MuiSvgIcon-root": {
                  color: "#FFD80A",
                },
                "& option": {
                  color: "#000",
                  fontSize: "0.9rem",
                },
                "& .MuiInputBase-input": {
                  textAlign: "center",
                },
              }}
              InputProps={{
                readOnly: rolling, // 🚀 Prevent typing
              }}
            >
              {Array.from(
                { length: maxTotal - minTotal + 1 },
                (_, i) => i + minTotal
              ).map((num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
            </TextField>

            <TextField
              label="Bet Amount"
              value={betAmount}
              size="small"
              fullWidth
              sx={{
                maxWidth: { xs: 100, sm: 150 },
                "& .MuiInputBase-root": {
                  height: { xs: 50, sm: 60 },
                  color: "#FFD80A",
                  background: "rgba(255, 255, 255, 0.1)",
                  borderRadius: 1,
                  fontSize: "2rem",
                  textAlign: "center",
                  fontWeight: "bolder",
                },
                "& .MuiInputLabel-root": {
                  color: "#FFD80A",
                },

                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#FFD80A",
                },
                "& .MuiSvgIcon-root": {
                  color: "#FFD80A",
                },
                "& .MuiInputBase-input": {
                  textAlign: "center",
                },
              }}
              InputProps={{ readOnly: true }}
            />
          </Box>
        </Box>

        <Box sx={{ textAlign: "center", mt: 2 }}>
          {/* Show Roll button only when NOT rolling */}
          {!rolling && (
            <Box
              component="img"
              src="/Roll.png"
              alt="Roll"
              sx={{
                width: 180,
                height: "auto",
                cursor: "pointer",
                transition: "transform 0.2s ease-in-out",
                "&:hover": {
                  transform: "scale(1.05)",
                },
              }}
              onClick={startRolling}
            />
          )}

          {/* Show countdown timer only when rolling */}
          {rolling && (
            <Box
              sx={{
                mt: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              {/* <Typography
                sx={{
                  fontSize: { xs: "16px", sm: "20px" },
                  fontWeight: "bold",
                  color: "#fff",
                  textShadow: "1px 1px 4px #000",
                }}
              >
                Auto-stopping in
              </Typography> */}

              {/* Timer Box */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "rgba(255, 255, 255, 0.4)", // Slight transparency
                  color: "red", // Yellow text for contrast
                  padding: "12px 24px", // Padding for spacing
                  borderRadius: "20px", // Rounded corners
                  boxShadow: "0 0 10px rgba(0, 0, 0, 0.3)", // Subtle gray shadow
                  fontSize: "38px", // Size of the text
                  fontWeight: "bold",
                  textAlign: "center",
                  textShadow: "2px 2px 8px rgba(0, 0, 0, 0.6)", // Shadow on text for visibility
                  letterSpacing: 1,
                }}
              >
                {/* Display time in mm:ss format */}
                {`${String(Math.floor(countdown / 60)).padStart(
                  2,
                  "0"
                )}:${String(countdown % 60).padStart(2, "0")}`}
                s
              </Box>
            </Box>
          )}
        </Box>

        <Grid container spacing={2} justifyContent="center" sx={{ mt: 3 }}>
          {Array.from({ length: diceCount }).map((_, idx) => (
            <Grid item key={idx}>
              <AnimatedDice
                rolling={rolling}
                finalValue={finalResults[idx] || 1}
              />
            </Grid>
          ))}
        </Grid>
        {/* {finalResults.length > 0 && !rolling && (
          <Fade in>
            <Typography
              variant="h6"
              align="center"
              color="primary"
              sx={{ mt: 2 }}
            >
            🎯 Total Score:{" "}
              <span style={{ fontSize: "1.5rem", color: "orange" }}>
                {total}
              </span>
            </Typography>
          </Fade>
        )} */}
      </Box>

      {/* Result Dialog */}
      <Dialog
        open={showResultDialog}
        TransitionComponent={Transition}
        keepMounted
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: "linear-gradient(90deg, #FFA500,rgb(199, 98, 40))",
            boxShadow: 10,
            border: `6px solid #000`,
          },
        }}
      >
        {loadingResult ? (
          // Show loader while waiting
          <Box
            sx={{
              py: 6,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <CircularProgress color="warning" size={60} thickness={5} />
            <Typography
              sx={{
                mt: 3,
                fontSize: "1.1rem",
                fontWeight: "bold",
                color: "#fff",
              }}
            >
              Rolling the dice...
            </Typography>
          </Box>
        ) : (
          <>
            <Fade in={showResultDialog} timeout={500}>
              <DialogTitle
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  // px: 3,
                  // pt: 3,
                  p: 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mt: 2,
                      mb: 3,
                    }}
                  >
                    <Box
                      sx={{
                        height: { xs: "80px", sm: "100px" },
                        width: "150px",
                      }}
                    >
                      <img
                        src={didWin ? "/youwin.png" : "/youlost.png"}
                        alt="Dice Roll Logo"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </DialogTitle>
            </Fade>

            <DialogContent sx={{ p: 0, textAlign: "center" }}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={6}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      mb: 3,
                    }}
                  >
                    <Box sx={{ width: "70px" }}>
                      <img
                        src="/selected.png"
                        alt="Selected"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        mt: 2,
                        width: 60,
                        height: 60,
                        background: "linear-gradient(45deg, orangered, orange)",
                        borderRadius: "50%",
                        color: "white",
                        fontWeight: "bold",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.3rem",
                        boxShadow: 3,
                      }}
                    >
                      {chosenNumber}
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={6} sm={6}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      mb: 3,
                    }}
                  >
                    <Box sx={{ width: "70px" }}>
                      <img
                        src="/totalscore.png"
                        alt="Total Score"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        mt: 2,
                        width: 60,
                        height: 60,
                        background:
                          chosenNumber === total
                            ? "linear-gradient(45deg, green, green)"
                            : "linear-gradient(45deg, orange, red)",
                        borderRadius: "50%",
                        color: "#fff",
                        fontWeight: "bold",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.3rem",
                        boxShadow: 3,
                      }}
                    >
                      {total}
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              <Grow in={showResultDialog} timeout={1000}>
                <Button
                  variant="outlined"
                  align="center"
                  sx={{
                    mx: { xs: 5, sm: 10 },
                    mt: 2,
                    fontWeight: "bolder",
                    letterSpacing: 0.5,
                    color: "#fff",
                  }}
                >
                  {didWin
                    ? `You earned ${winAmount} credits`
                    : `You lost ${betAmount} credits`}
                </Button>
              </Grow>
            </DialogContent>

            <DialogActions sx={{ px: 3 }}>
              <Grow in={showResultDialog} timeout={1200}>
                <Button
                  onClick={() => setShowResultDialog(false)}
                  fullWidth
                  sx={{
                    "&:hover": {
                      backgroundColor: "transparent", // Remove hover background color
                      opacity: 1, // Ensure opacity remains 1
                    },
                    backgroundColor: "transparent", // Set initial background to transparent
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mb: 3,
                    }}
                  >
                    <Box
                      sx={{
                        width: "220px",
                      }}
                    >
                      <img
                        src={didWin ? "/playagain.png" : "/tryagain.png"}
                        alt="Dice Roll Logo"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                    </Box>
                  </Box>
                </Button>
              </Grow>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default DiceRoller;
